import type { CSSProperties } from 'react'
import type { ECharts, EChartsOption } from 'echarts'
import type { MergeWithCustomizer } from 'lodash'
import type { ChartEvts } from './declare'
import { useEffect, useMemo, useRef } from 'react'
import { EasyECharts } from 'src/components'
import { $consts } from 'src/plugins'
import { set, mergeWith } from 'lodash'

type Props = {
  notMerge?: boolean,
  lazyUpdate?: boolean,
  chartData?: any,
  extraOption?: EChartsOption, // effect 渲染后的 setOption
  mergeOption?: EChartsOption, // memo 运行时 merge 的option
  mergeWith?: MergeWithCustomizer,
  onChartReady?: Function,
  autoPlay?: boolean,
  autoPlayTime?: number,
  onEvts?: ChartEvts,
  style?: CSSProperties
}

const ChartLineBar = (props: Props) => {
  const chart = useRef<ECharts>()
  const handleChartReady = (chartInstance: any) => {
    chart.current = chartInstance
    props.onChartReady?.(chartInstance)
  }
  useEffect(() => {
    if (props.extraOption && chart.current) {
      chart.current.setOption(props.extraOption, undefined, true)
    }
  }, [props.extraOption])

  const getSpecialOption = (chartType: number) => {
    if (chartType === $consts['TABLE/CHART_TYPE_BAR']) {
      return {
        barMaxWidth: 30
      }
    }
    return {}
  }

  const dataOption = useMemo<any>(() => {
    if (!props.chartData) return undefined

    const { Name: title, XAxis = [], YAxis = [], Data = [] } = props.chartData
    const legend: any = {
      type: 'scroll',
      width: '70%',
      selected: {},
      data: []
    }
    const xAxis = XAxis.map((item: any) => ({
      type: 'category',
      axisLabel: { width: 80, overflow: 'break' },
      data: item.Data,
    }))
    const yAxis = YAxis.map((item: any) => ({
      type: 'value',
      name: item.Name || title,
      nameTextStyle: { align: 'left' },
      max: item.Max || undefined,
      min: item.Min,
      inverse: item.IsInverse
    }))
    const series = Data.map((item: any, index: number) => {
      const {
        Name = '图例',
        ChartType,
        XAxisIndex,
        YAxisIndex,
        Stack,
        Color,
        LegendSelected,
        Data: SeriesData = []
      } = item
      // 图例名称
      legend.data.push(Name)
      // 图例默认选中
      legend.selected[Name] = LegendSelected !== undefined ? LegendSelected : true
      // X 轴 boundaryGap 左右留白策略
      const xAxisIndex = XAxisIndex || index
      xAxis[xAxisIndex] && set(xAxis[xAxisIndex], 'boundaryGap', ChartType === $consts['TABLE/CHART_TYPE_BAR'])

      return {
        type: ChartType === $consts['TABLE/CHART_TYPE_LINE'] ? 'line' : 'bar',
        name: Name,
        yAxisIndex: YAxisIndex,
        stack: Stack,
        itemStyle: { color: Color },
        ...getSpecialOption(ChartType),
        data: SeriesData.map((data: any) => ({
          value: data.Value
        }))
      }
    })

    return {
      legend,
      grid: {
        right: 30,
        bottom: 10,
        left: 20,
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
      },
      toolbox: {
        show: true,
        orient: 'vertical',
        right: 0,
        top: 'center',
        feature: {
          magicType: { show: true, type: ['line', 'bar', 'stack'] },
          saveAsImage: { show: true }
        }
      },
      dataZoom: [{ type: 'inside' }],
      xAxis,
      yAxis,
      series
    }
  }, [props.chartData])

  const chartOption = useMemo(() => {
    if (!props.mergeOption) return dataOption
    return mergeWith(dataOption, props.mergeOption, props.mergeWith)
  }, [dataOption, props.mergeOption])

  return (
    <EasyECharts
      notMerge={props.notMerge ?? true}
      lazyUpdate={props.lazyUpdate ?? true}
      autoPlay={props.autoPlay}
      autoPlayTime={props.autoPlayTime}
      option={chartOption}
      onChartReady={handleChartReady}
      onEvents={props.onEvts}
      style={props.style}
    />
  )
}

export default ChartLineBar